import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Input } from '../../../../components/ui/Input/Input';
import { Select } from '../../../../components/ui/Select/Select';
import { MultiSearchSelect } from '../../../../components/ui/MultiSearchSelect/MultiSearchSelect';
import { SearchableSelect } from '../../../../components/ui/SearchableSelect/SearchableSelect';
import { useMunicipioSearch } from '../../../../hooks/useMunicipioSearch';
import { addressService } from '../../../address/services/addressService';
import { buscarEnderecoCompleto } from '../../core/services/enderecoResolverService';
import { CepOpcoes } from '../../../address/components/CepOpcoes';
import { escolherRegistroCep, criteriosCepDoFormulario } from '../../../address/utils/cepRegistros';
import { unformat, formatCurrency } from '../../../../utils/formatters';

const formatCPF = (value) => {
  const cpf = value.replace(/\D/g, '');
  if (cpf.length <= 11) {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4').slice(0, 14);
  }
  return value;
};

const formatCNPJ = (value) => {
  const cnpj = value.replace(/\D/g, '');
  if (cnpj.length <= 14) {
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5').slice(0, 18);
  }
  return value;
};

const formatPhone = (value) => {
  const phone = value.replace(/\D/g, '');
  if (phone.length <= 10) {
    return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

const formatCEP = (value) => {
  const cep = value.replace(/\D/g, '');
  if (cep.length <= 8) {
    return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
  }
  return value;
};

// Checkbox com aparência 100% customizada (appearance: none + marca desenhada
// via ::after), em vez do checkbox nativo do SO com só width/height + accentColor.
// Motivo: no Chromium/Windows, o widget nativo desenhado pro estado marcado não
// respeita width/height do mesmo jeito que o desmarcado — resultado visível eram
// checkboxes de tamanhos diferentes lado a lado (uns maiores que outros) na mesma
// seção de Declarações Obrigatórias, dependendo de quais já estavam marcados.
const CheckboxInput = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  -webkit-appearance: none;
  margin: 0;
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  flex-shrink: 0;
  box-sizing: border-box;
  border: 2px solid #94a3b8;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  background: #fff;

  &:checked {
    background: #0d2257;
    border-color: #0d2257;
  }

  &:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 0px;
    width: 5px;
    height: 9px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

// Precisam ser componentes nomeados em PascalCase (não arrow functions dentro
// do objeto fieldComponents) porque chamam Hooks — o eslint-plugin-react-hooks
// só reconhece uma função como componente/Hook pelo nome dela (maiúscula
// inicial, ou prefixo "use"), não pela posição onde é usada.
// Campo de CEP dos requerimentos. Sem `field.enderecoFields`, é só um input com
// máscara (comportamento original, sem busca nenhuma). Com `field.enderecoFields`
// (ver enderecoSections.js/sociedadeSections.js), busca o CEP no blur e autopreenche
// os campos irmãos — que ficam `disabled` (só são preenchidos por essa busca, nunca
// digitados à mão, pra nunca divergir do que a API de Endereços devolveu).
//
// CEP com mais de um registro (CEP único de localidade, ex: 79290-000): mostra as
// opções em CepOpcoes e só preenche depois da escolha. Grava no formData:
// - `{name}Id`: ID do registro escolhido, usado na gravação pra resolver os IDs
//   certos (ver criteriosCepDoFormulario em enderecoResolverService.js);
// - `{name}Pendente`: true enquanto há opções sem escolha (trava o "Próximo", ver
//   validateField em validation.js).
function CepField({ field, value, error, touched, onChange, onBlur, setErrors, formData }) {
  const [opcoes, setOpcoes] = useState([]);
  const campoId = `${field.name}Id`;
  const campoPendente = `${field.name}Pendente`;
  const set = (name, novoValor) => onChange({ target: { name, value: novoValor } });

  const preencher = (registro) => {
    const { logradouro, bairro, municipio, estado, pais } = field.enderecoFields;
    set(logradouro, registro.logradouro || '');
    set(bairro, registro.bairro || '');
    if (municipio) set(municipio, registro.cidade || '');
    if (estado) set(estado, registro.estado || '');
    // CEP é sempre brasileiro: se a API não devolver o país, não deixa o campo
    // (travado e obrigatório) vazio, senão a pessoa não consegue avançar
    if (pais) set(pais, registro.pais || 'Brasil');
    set(campoId, registro.id ?? '');
    set(campoPendente, false);
    setErrors?.(prev => ({ ...prev, [field.name]: null }));
  };

  // O País fica como está (Brasil) — só os campos que dependem do CEP são limpos
  const limpar = () => {
    const { logradouro, bairro, municipio, estado } = field.enderecoFields;
    [logradouro, bairro, municipio, estado].filter(Boolean).forEach((nome) => set(nome, ''));
    set(campoId, '');
  };

  const buscar = async (cep) => {
    const resultado = await buscarEnderecoCompleto(cep);

    if (resultado.status === 'encontrado' && resultado.registros.length === 1) {
      setOpcoes([]);
      preencher(resultado.registros[0]);
    } else if (resultado.status === 'encontrado') {
      setOpcoes(resultado.registros);
      // Mesmo CEP buscado de novo (ex: a pessoa só passou pelo campo): mantém a escolha
      if (resultado.registros.some((r) => String(r.id) === String(formData?.[campoId]))) return;
      limpar();
      set(campoPendente, true);
      setErrors?.(prev => ({ ...prev, [field.name]: null }));
    } else {
      setOpcoes([]);
      limpar();
      set(campoPendente, false);
      setErrors?.(prev => ({
        ...prev,
        [field.name]: resultado.status === 'nao_encontrado'
          ? 'CEP não encontrado em nossa base. Entre em contato com um atendente da OAB/MS para verificar esse CEP.'
          : 'Não foi possível verificar o CEP agora. Tente novamente em instantes.'
      }));
    }
  };

  // CEP que já chega preenchido sem registro escolhido (veio do perfil/cadastro, ou
  // rascunho retomado com a escolha pendente): confere o CEP na montagem do campo,
  // senão a gravação não teria como saber qual registro usar (ver criteriosCepDoFormulario).
  // - 1 registro, ou vários com logradouro/bairro do formulário batendo com um só:
  //   guarda o ID em silêncio, sem mexer nos campos que vieram do perfil;
  // - vários e ambíguo: mostra a lista e trava o "Próximo" até a pessoa escolher.
  // Com `{name}Id` já preenchido (escolha feita antes), não faz nada.
  useEffect(() => {
    if (!field.enderecoFields || unformat(value || '').length !== 8) return;
    if (formData?.[campoId] && !formData?.[campoPendente]) return;

    let ativo = true;
    buscarEnderecoCompleto(value).then((resultado) => {
      if (!ativo || resultado.status !== 'encontrado') return;

      const escolhido = escolherRegistroCep(resultado.registros, criteriosCepDoFormulario(formData, field.name));
      if (escolhido) {
        setOpcoes(resultado.registros.length > 1 ? resultado.registros : []);
        set(campoId, escolhido.id ?? '');
        set(campoPendente, false);
      } else {
        setOpcoes(resultado.registros);
        set(campoPendente, true);
      }
    });
    return () => { ativo = false; };
    // Só na montagem do campo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBlur = (e) => {
    onBlur(e);
    if (!field.enderecoFields) return;

    if (unformat(value || '').length !== 8) {
      setOpcoes([]);
      return;
    }

    buscar(value);
  };

  return (
    <>
      <Input
        label={field.label}
        name={field.name}
        value={value || ''}
        onChange={(e) => onChange({
          target: { name: field.name, value: formatCEP(e.target.value) }
        })}
        onBlur={handleBlur}
        error={touched && error}
        placeholder="00000-000"
        maxLength={9}
        required={field.required}
        disabled={field.disabled}
        helper={field.enderecoFields && !error && 'Digite e saia do campo para buscar o endereço'}
      />
      <CepOpcoes
        name={field.name}
        opcoes={opcoes}
        selecionadoId={formData?.[campoId]}
        onSelect={preencher}
        disabled={field.disabled}
      />
    </>
  );
}

function MunicipioBuscaField({ field, value, error, touched, onChange, onBlur, formData }) {
  const { buscarMunicipios, minChars } = useMunicipioSearch();
  const labelField = `${field.name}Label`;
  const getMunicipioLabel = (mun) => (mun ? `${mun.nome} - ${mun.estadoSigla}` : '');

  return (
    <SearchableSelect
      label={field.label}
      name={field.name}
      value={value}
      valueLabel={formData?.[labelField]}
      onChange={onChange}
      onSelectOption={(mun) => onChange({ target: { name: labelField, value: getMunicipioLabel(mun) } })}
      onSearch={buscarMunicipios}
      minChars={minChars}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled}
      placeholder={field.placeholder || 'Digite o nome da cidade...'}
      getOptionLabel={getMunicipioLabel}
      getOptionValue={(mun) => (mun ? String(mun.id) : '')}
      noOptionsMessage="Nenhuma cidade encontrada"
    />
  );
}

function PaisSelectField({ field, value, error, touched, onChange, onBlur }) {
  const [paises, setPaises] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ativo = true;
    addressService.getCountries()
      .then((response) => { if (ativo) setPaises(response.data || []); })
      .finally(() => { if (ativo) setLoading(false); });
    return () => { ativo = false; };
  }, []);

  return (
    <Select
      label={field.label}
      name={field.name}
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled || loading}
    >
      <option value="">{loading ? 'Carregando...' : (field.placeholder || 'Selecione...')}</option>
      {paises.map((pais) => (
        <option key={pais.id} value={pais.id}>{pais.nome}{pais.sigla ? ` (${pais.sigla})` : ''}</option>
      ))}
    </Select>
  );
}

const fieldComponents = {
  text: ({ field, value, error, touched, onChange, onBlur }) => (
    <Input
      label={field.label}
      name={field.name}
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled}
      placeholder={field.placeholder}
    />
  ),

  select: ({ field, value, error, touched, onChange, onBlur }) => (
    <Select
      label={field.label}
      name={field.name}
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled}
    >
      <option value="">{field.placeholder || 'Selecione...'}</option>
      {(field.options || []).map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </Select>
  ),

  multiSelect: ({ field, value, error, touched, onChange, onBlur }) => (
    <MultiSearchSelect
      label={field.label}
      name={field.name}
      value={value || []}
      onChange={onChange}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled}
      placeholder={field.placeholder}
      options={field.options || []}
    />
  ),

  date: ({ field, value, error, touched, onChange, onBlur }) => (
    <Input
      type="date"
      label={field.label}
      name={field.name}
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled}
    />
  ),

  number: ({ field, value, error, touched, onChange, onBlur }) => (
    <Input
      type="number"
      label={field.label}
      name={field.name}
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled}
      placeholder={field.placeholder}
      min={field.min}
      max={field.max}
      step={field.integer ? 1 : field.step}
    />
  ),

  cpf: ({ field, value, error, touched, onChange, onBlur }) => (
    <Input
      label={field.label}
      name={field.name}
      value={value || ''}
      onChange={(e) => onChange({
        target: { name: field.name, value: formatCPF(e.target.value) }
      })}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled}
      placeholder="000.000.000-00"
      maxLength={14}
    />
  ),

  cnpj: ({ field, value, error, touched, onChange, onBlur }) => (
    <Input
      label={field.label}
      name={field.name}
      value={value || ''}
      onChange={(e) => onChange({
        target: { name: field.name, value: formatCNPJ(e.target.value) }
      })}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled}
      placeholder="00.000.000/0000-00"
      maxLength={18}
    />
  ),

  phone: ({ field, value, error, touched, onChange, onBlur }) => (
    <Input
      label={field.label}
      name={field.name}
      value={value || ''}
      onChange={(e) => onChange({
        target: { name: field.name, value: formatPhone(e.target.value) }
      })}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled}
      placeholder={field.placeholder || '(00) 00000-0000'}
    />
  ),

  // Valor em reais com máscara ("R$ 10.000,00") — guarda o texto formatado, que
  // a API de Cadastro/Login aceita como está em valorCapitalSocial.
  currency: ({ field, value, error, touched, onChange, onBlur }) => (
    <Input
      label={field.label}
      name={field.name}
      value={value || ''}
      onChange={(e) => onChange({
        target: { name: field.name, value: formatCurrency(e.target.value) }
      })}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled}
      placeholder={field.placeholder || 'R$ 0,00'}
      inputMode="numeric"
    />
  ),

  // Input de CEP com máscara; com `field.enderecoFields`, busca e autopreenche o endereço (ver CepField).
  cep: CepField,

  // Autocomplete de município por nome (busca ao vivo na API de Endereços) —
  // usado em campos como Naturalidade/Município do Título de Eleitor, onde o
  // valor certo é o ID do município (não o texto digitado). Guarda o ID em
  // `field.name` e o rótulo de exibição em `field.name + 'Label'` (necessário
  // porque a busca é assíncrona: o SearchableSelect não tem como redescobrir
  // sozinho o texto de um ID já salvo).
  municipioBusca: MunicipioBuscaField,

  // Select de País, carregado uma vez da API de Endereços (lista pequena, sem
  // busca) — usado no campo Nacionalidade.
  paisSelect: PaisSelectField,

  email: ({ field, value, error, touched, onChange, onBlur }) => (
    <Input
      type="email"
      label={field.label}
      name={field.name}
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled}
      placeholder={field.placeholder || 'email@exemplo.com'}
    />
  ),

  radio: ({ field, value, onChange }) => (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ marginBottom: '0.5rem', display: 'block', fontWeight: 500 }}>
        {field.label} {field.required && <span style={{ color: '#ff4444' }}>*</span>}
      </label>
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
        {field.options.map(opt => (
          <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="radio"
              name={field.name}
              value={opt.value}
              checked={value === opt.value}
              onChange={onChange}
              style={{ accentColor: '#0d2257' }}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  ),

  // `field.clearFieldsOnUncheck` (opcional): lista de outros campos que voltam a
  // `false` assim que este checkbox é desmarcado — usado quando um 2º checkbox só
  // faz sentido "dentro" da concordância deste (ex: cadastrarNaAASP só existe se
  // concordaDivulgacaoListagens estiver marcado). Reseta o valor de verdade em
  // formData (não só a visibilidade via showWhen), pra nunca reenviar um "true"
  // antigo que ficou escondido depois que a pessoa desmarcou o campo pai.
  checkbox: ({ field, value, onChange, error, touched }) => (
    <div style={{ marginBottom: '1rem' }}>
      {/* flex-start + marginTop: alinha o checkbox à 1ª linha de declarações longas */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
        <CheckboxInput
          style={{ marginTop: '0.15rem' }}
          name={field.name}
          checked={value === true}
          onChange={(e) => {
            const checked = e.target.checked;
            onChange({ target: { name: field.name, value: checked } });
            if (!checked && field.clearFieldsOnUncheck) {
              field.clearFieldsOnUncheck.forEach((nome) => onChange({ target: { name: nome, value: false } }));
            }
          }}
        />
        <span style={{ fontWeight: 500 }}>{field.label}</span>
      </label>
      {touched && error && <small style={{ color: '#ff4444', fontSize: '0.8rem' }}>{error}</small>}
    </div>
  ),

  textarea: ({ field, value, error, touched, onChange, onBlur }) => (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{
        marginBottom: '0.5rem',
        display: 'block',
        fontWeight: 500,
        color: '#1e293b'
      }}>
        {field.label} {field.required && <span style={{ color: '#ff4444' }}>*</span>}
      </label>
      <textarea
        name={field.name}
        value={value || ''}
        onChange={onChange}
        placeholder={field.placeholder}
        rows={field.rows || 4}
        style={{
          width: '100%',
          padding: '0.75rem',
          borderRadius: '8px',
          border: `1px solid ${touched && error ? '#ff4444' : '#e2e8f0'}`,
          // 16px no mínimo evita o zoom automático do iOS ao focar
          fontSize: 'max(16px, 0.9rem)',
          fontFamily: 'inherit',
          resize: 'vertical',
          transition: 'all 0.2s',
          outline: 'none',
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#0d2257';
          e.target.style.boxShadow = '0 0 0 3px rgba(13, 34, 87, 0.1)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = touched && error ? '#ff4444' : '#e2e8f0';
          e.target.style.boxShadow = 'none';
          onBlur(e);
        }}
      />
      {touched && error && (
        <small style={{ color: '#ff4444', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
          {error}
        </small>
      )}
      {field.maxLength && (
        <small style={{ color: '#64748b', fontSize: '0.7rem', marginTop: '0.25rem', display: 'block' }}>
          Máximo de {field.maxLength} caracteres
        </small>
      )}
    </div>
  ),

  dataEmissaoCNH: ({ field, value, error, touched, onChange, onBlur }) => (
    <Input
      type="date"
      label={field.label}
      name={field.name}
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      error={touched && error}
      required={field.required}
      disabled={field.disabled}
    />
  )
};

export default fieldComponents;